import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateKycCase } from "@/features/kyc/api/use-create-kyc-case";
import type { CreateKycCaseRequestDto } from "@/features/kyc/api/dto";
import {
  createKycCaseSchema,
  type KycCaseFormValues,
} from "@/features/kyc/schemas/kyc-case.schema";
import { WALLET_KYC_LEVELS } from "@/features/shared/api/enums";
import {
  useAgentSelectOptions,
  usePartySelectOptions,
} from "@/features/shared/api";

const EMPTY_VALUES: KycCaseFormValues = {
  partyId: "",
  levelRequested: "TIER1",
  assignedAgentId: "",
};

/** Chaîne vide → undefined : sans agent, le canal reste SELF_SERVICE côté serveur. */
const toPayload = (values: KycCaseFormValues): CreateKycCaseRequestDto => ({
  partyId: values.partyId,
  levelRequested: values.levelRequested,
  assignedAgentId: values.assignedAgentId?.trim() || undefined,
});

/**
 * Formulaire d'ouverture d'un dossier KYC (POST /KycCases/add, idempotent) —
 * TanStack Form + schéma zod mémoïsé sur la langue courante (règle projet :
 * jamais d'instance figée).
 */
export function KycCaseFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const schema = useMemo(() => createKycCaseSchema(), [i18n.language]);

  const createKycCase = useCreateKycCase();
  const [preview, setPreview] = useState<CreateKycCaseRequestDto | null>(null);
  const pending = createKycCase.isPending;
  const { options: partyOptions, isLoading: partiesLoading } =
    usePartySelectOptions(open);
  const { options: agentOptions, isLoading: agentsLoading } =
    useAgentSelectOptions(open);

  // Radix démonte le contenu du Dialog à la fermeture : à chaque ouverture,
  // le formulaire repart des valeurs vides.
  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  const fieldError = (name: keyof KycCaseFormValues) => (props: { value: unknown }) => {
    const shape = schema.shape[name];
    const result = shape.safeParse(props.value ?? "");
    return result.success ? undefined : result.error.issues[0]?.message;
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("kyc.cases.create")}</DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="partyId" validators={{ onChange: fieldError("partyId") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="partyId">{t("kyc.cases.form.partyId")}</Label>
                <EntitySearchableSelect
                  id="partyId"
                  options={partyOptions}
                  value={field.state.value ?? ""}
                  onValueChange={(value) => field.handleChange(value)}
                  placeholder={t("kyc.cases.form.partyIdPlaceholder")}
                  isLoading={partiesLoading}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field
            name="levelRequested"
            validators={{ onChange: fieldError("levelRequested") }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label>{t("kyc.cases.form.levelRequested")}</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) =>
                    field.handleChange(v as KycCaseFormValues["levelRequested"])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WALLET_KYC_LEVELS.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field
            name="assignedAgentId"
            validators={{ onChange: fieldError("assignedAgentId") }}
          >
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="assignedAgentId">
                  {t("kyc.cases.form.assignedAgentId")}
                </Label>
                <EntitySearchableSelect
                  id="assignedAgentId"
                  options={agentOptions}
                  value={field.state.value ?? ""}
                  onValueChange={(value) => field.handleChange(value)}
                  placeholder={t("kyc.cases.form.assignedAgentIdPlaceholder")}
                  isLoading={agentsLoading}
                  clearable
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <MutationConfirmPreview
      open={preview !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) setPreview(null);
      }}
      data={preview}
      title={t("common.mutationPreview.createTitle")}
      description={t("common.mutationPreview.createDescription")}
      t={(key) => t(key as never)}
      pending={pending}
      onConfirm={() => {
        if (!preview) return;
        createKycCase.mutate(preview, {
          onSuccess: () => {
            notifSilence(t("kyc.cases.feedback.created"));
            setPreview(null);
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        });
      }}
    />
    </>
  );
}
